// Analytics + Consent — zwei Ebenen:
//
// 1. ANALYTICS_MODE (hier im Code): globale Notbremse, nur "on" oder "off".
//    "off" schaltet Tracking und Banner für ALLE Tenants ab, unabhängig von
//    deren Konfiguration. Nur per Deploy änderbar.
//
// 2. APP_TenantConfig.analyticsMode: der eigentliche Modus, pro Tenant im
//    Admin-Dashboard umschaltbar:
//
//      "off"       — kein Tracking, kein Banner, keine Schreibzugriffe auf
//                    localStorage/sessionStorage.
//      "anonymous" — anonyme Reichweitenmessung (Art. 6 Abs. 1 lit. f DSGVO).
//                    Keine Identifier, kein Banner, nichts im Browser des
//                    Besuchers. Erfasst pro Aufruf: tenant, hostname, pageName,
//                    path, eventType, language (grob), deviceClass (grob).
//      "full"      — anonyme Basis PLUS Opt-in-Banner für erweitertes Tracking
//                    (visitorId, sessionId, userAgent, referrer, Bildschirm,
//                    Verweildauer) nach Art. 6 Abs. 1 lit. a DSGVO + § 25 TTDSG.
//
// Der effektive Modus ist immer das Minimum aus beiden Ebenen.

export type AnalyticsKillSwitch = "on" | "off";

export const ANALYTICS_MODE = "on" as AnalyticsKillSwitch;

export type TenantAnalyticsMode = "off" | "anonymous" | "full";

/**
 * Effektiver Modus für den aktuell geladenen Tenant.
 *
 * Der Zugriff auf den State erfolgt bewusst lazy per require(), damit dieses
 * Config-Modul keine Import-Abhängigkeit auf state.ts bekommt (gleiches Muster
 * wie in AnalyticsTracker).
 *
 * Ist noch keine tenantConfig geladen, gilt "off" — sicherer Default. In der
 * Praxis tritt das nicht auf: AppProvider rendert die App erst, wenn der State
 * fertig geladen ist.
 */
export function getTenantAnalyticsMode(): TenantAnalyticsMode {
  if (ANALYTICS_MODE === "off") return "off";

  const { app } = require("../state");
  const tenantConfig = app.store.getState()?.tenantConfig;
  if (!tenantConfig) return "off";

  const mode = tenantConfig.get("analyticsMode");
  if (mode === "off" || mode === "anonymous" || mode === "full") {
    return mode;
  }

  // Fallback für Tenants, die nur das ältere Boolean-Feld gesetzt haben.
  // Dadurch verhalten sich Bestandstenants unverändert, egal in welcher
  // Reihenfolge Schema, Cloud und Web deployt werden.
  return tenantConfig.get("analyticsEnabled") === true ? "anonymous" : "off";
}

export function isAnalyticsEnabled(): boolean {
  return getTenantAnalyticsMode() !== "off";
}

export function isConsentBannerEnabled(): boolean {
  return getTenantAnalyticsMode() === "full";
}
